import { Button, Form, Input, Popconfirm } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { cardApi } from '../../../../../apis'
import { AlertMessageEnum } from '../../../../../common'
import {
  LoadingDataComponent,
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { ApiMethodEnum, CardEntity, UserTypeEnum } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadCards,
  loadProfile,
  setActionProcessing,
  setAlert,
  setShowManageModal,
  setShowPayInModal,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

export const PayInModalComponent: React.FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { profile } = useSelector((state: RootState) => state.auth)
  const { apis } = useSelector((state: RootState) => state.api)
  const { management } = useSelector((state: RootState) => state.common)
  const { selectedCardId } = useSelector((state: RootState) => state.card)
  const [form] = Form.useForm()
  const [card, setCard] = React.useState<CardEntity>()
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (selectedCardId) {
      const loadData = async () => {
        try {
          setLoading(true)
          const resp = await cardApi.getCardById(selectedCardId)
          setCard(resp.data)
        } catch (e) {
          dispatch(
            setAlert({
              key: 'card.loadData.fail',
              type: AlertMessageEnum.ERROR,
            })
          )
        } finally {
          setLoading(false)
        }
      }
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onFinish = async () => {
    if (selectedCardId) {
      try {
        dispatch(setActionProcessing(true))
        dispatch(setAlert())
        await cardApi.payInCard(
          selectedCardId,
          form.getFieldValue('payInAmount')
        )
        dispatch(loadCards())
        dispatch(
          setAlert({
            key: 'card.payIn.success',
            type: AlertMessageEnum.SUCCESS,
          })
        )
      } catch (e) {
        if (e.response && e.response.status && e.response.status === 401) {
          dispatch(loadProfile())
          await cardApi.payInCard(
            selectedCardId,
            form.getFieldValue('payInAmount')
          )
          dispatch(
            setAlert({
              key: 'card.payIn.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } else {
          dispatch(
            setAlert({
              key: 'card.payIn.fail',
              type: AlertMessageEnum.ERROR,
            })
          )
        }
      } finally {
        dispatch(setActionProcessing(false))
      }
    }
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {loading && <LoadingDataComponent />}
      {!loading &&
        card &&
        card.customer &&
        (!management.alertMessage ||
          (management.alertMessage &&
            management.alertMessage.type !== AlertMessageEnum.SUCCESS)) && (
          <>
            <Form.Item
              label={
                <StyledLabel>{translate('card.id', language)}</StyledLabel>
              }
            >
              {card.id}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('common.customer', language)}
                </StyledLabel>
              }
            >
              {card.customer.name}
            </Form.Item>
            <StyledFormItem
              name="payInAmount"
              label={
                <StyledLabel>
                  {translate('common.amount', language)}
                </StyledLabel>
              }
              rules={[
                {
                  required: true,
                  message: '',
                },
                () => ({
                  validator(_, value) {
                    if (!value || value < 1) {
                      return Promise.reject(
                        new Error(
                          translate('card.payIn.amount.invalid', language)
                        )
                      )
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                type="number"
                placeholder={translate('common.amount', language)}
                allowClear
              />
            </StyledFormItem>
          </>
        )}
      {!loading && (
        <ManageModalFooterComponent
          onClose={() => {
            dispatch(setAlert())
            dispatch(setShowPayInModal(false))
            dispatch(setShowManageModal(true))
          }}
          customButtons={
            <>
              {profile &&
                (profile.userType.type === UserTypeEnum.ADMIN ||
                  apis.filter(
                    (item) =>
                      item.path === location.pathname &&
                      item.method === ApiMethodEnum.PUT
                  ).length > 0) &&
                (!management.alertMessage ||
                  (management.alertMessage &&
                    management.alertMessage.type !==
                      AlertMessageEnum.SUCCESS)) && (
                  <Popconfirm
                    title={translate('card.payIn.confirm', language)}
                    okText={translate('common.yes', language)}
                    cancelText={translate('common.no', language)}
                    placement="topRight"
                    onConfirm={() => form.submit()}
                  >
                    <Button
                      type="primary"
                      loading={management.actionProcessing}
                    >
                      {translate(
                        `card.${
                          management.actionProcessing ? 'recharging' : 'payIn'
                        }`,
                        language
                      )}
                    </Button>
                  </Popconfirm>
                )}
            </>
          }
        />
      )}
    </Form>
  )
  return (
    <ManageModalComponent
      body={body}
      title={translate('card.payIn.title', language)}
    />
  )
}
